import React, { FC } from 'react';

import ALink from '~/components/features/custom-link';
import { useProgram, useGeneral } from 'hooks';
import { CatalogueItem } from '../../../utils/types/catalogueItem';
import { trackQuickView } from '~/sdk';
import { awardHref, cardPoints } from '~/utils/productGroup';
import styles from './product-two.module.scss';

type Props = {
  item: CatalogueItem;
  adClass?: string;
  toggleWishlist?: (item: CatalogueItem) => void;
  wishlist?: CatalogueItem[];
  addToCart?: (item: CatalogueItem) => void;
  openQuickview?: (slug: string) => void;
  isCategory?: boolean;
  isNew?: boolean;
  isTop?: boolean;
  // Carga ansiosa (sin lazy) + prioridad alta: usar en la primera fila visible
  // para que el "above the fold" aparezca de inmediato (LCP).
  eager?: boolean;
};

// Card de producto (from-scratch, estetica nueva). Compartida por browse,
// search y productos relacionados. Aprovecha data ya disponible:
//  - item.points -> pill prominente
//  - item.catalogs -> badge de catalogo especial (Navidad, etc.)
// Jerarquia: marca (arriba, pequeña) / nombre (protagonista) / modelo
// (secundario pero legible, porque es el que identifica el producto).
const ProductTwo: FC<Props> = ({ item, adClass = '', eager = false }) => {
  const { coinName } = useProgram();
  const { openModal } = useGeneral();

  const { award } = item;
  // Producto con variantes: una tarjeta, «Desde X» si los puntos varían y el
  // detalle entra sin preseleccionar.
  const { href, as } = awardHref(item);
  const price = cardPoints(item);
  const templateId = item.groupSummary?.templateId ?? item.group?.templateId;
  const catalogs = item.catalogs ?? [];

  const onQuickview = (e: React.MouseEvent) => {
    e.preventDefault();
    openModal(item);
    trackQuickView({
      productId: String(award.id),
      productPoints: item.points,
      templateId: templateId != null ? String(templateId) : undefined,
    });
  };

  return (
    <div className={`${styles.card} ${adClass}`}>
      <div className={styles.media}>
        <ALink href={href} as={as} className={styles.mediaLink}>
          {/* <img> nativo: el navegador maneja el lazy (con su propio margen de
              preload) y reusa el HTTP cache → las cacheadas aparecen al instante,
              sin fade. La primera fila va eager + fetchPriority alta (LCP). */}
          <img
            className={styles.img}
            alt={award.name}
            src={award.mainImage}
            loading={eager ? 'eager' : 'lazy'}
            decoding="async"
            fetchPriority={eager ? 'high' : 'auto'}
          />
        </ALink>

        {/* Badge superior: catalogo especial */}
        {catalogs.length > 0 && (
          <div className={styles.topLeft}>
            <span className={styles.catBadge}>
              <i className="fas fa-star"></i>
              {catalogs[0].name}
            </span>
            {catalogs.length > 1 && <span className={styles.catMore}>+{catalogs.length - 1}</span>}
          </div>
        )}

        {/* Puntos como pill prominente */}
        <span className={styles.pointsPill}>
          {price.from && <span className={styles.from}>Desde</span>}
          <strong>{price.points.toLocaleString('es')}</strong>
          <span className={styles.coin}>{coinName}</span>
        </span>

        {/* Hover: CTA de vista rapida */}
        <div className={styles.overlay}>
          <button type="button" className={styles.quickview} onClick={onQuickview}>
            <i className="fas fa-eye"></i>
            <span>Vista rápida</span>
          </button>
        </div>
      </div>

      <div className={styles.info}>
        {award.brand?.name && <span className={styles.brand}>{award.brand.name}</span>}

        <ALink href={href} as={as} className={styles.name}>
          {award.name}
        </ALink>

        {award.model && <span className={styles.model}>{award.model}</span>}
      </div>
    </div>
  );
};

export default ProductTwo;
